import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { Link } from 'react-router-dom';
import '../styles/MainPage.css';
import IngestOptionsDialog from '../components/IngestOptionsDialog';
// IngestionGroupEditor removed - CRUD operations only available in Settings > Ingestion
import ExecutionQueue from '../components/ExecutionQueue';
import { API_BASE_URL, ingestGroup, getIngestionProgress, authenticatedFetch, getAuthHeaders } from '../utils/api';
import { buildIngestionResultMessage } from '../utils/ingestionProgressMessage';
import { useGroup } from '../contexts/GroupContext';

// Use shared API_BASE_URL from utils (defaults to current origin if env not set)

function isApiSourceGroup(group) {
  return group?.config?.type === 'api_source';
}

function MainPage() {
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
    appendGroupToUrl,
    activeSuffix,
  } = useGroup();
  const [promptButtons, setPromptButtons] = useState([]);
  // const [executingPromptId, setExecutingPromptId] = useState(null);
  const [, setActiveRequests] = useState([]); // [{ id, name }]
  const [resultMessage, setResultMessage] = useState(null);
  const [executingPromptId, setExecutingPromptId] = useState(null);
  const promptsLoadedRef = useRef(false);
  const promptsLoadingRef = useRef(false);

  // Ingestion Groups state (moved from Settings to Main)
  const [allPrompts, setAllPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [ingestLoadingByGroup, setIngestLoadingByGroup] = useState({}); // { [slug]: boolean }
  const [ingestProgressByGroup, setIngestProgressByGroup] = useState({}); // { [slug]: { percent, status, processed, total } }
  const ingSuffix = activeSuffix || 'dev';
  // Ingest options dialog state
  const [ingestDialogSlug, setIngestDialogSlug] = useState(null); // null = closed, string = group slug
  // Review stats per group (from /data-review/stats)
  const [groupReviewStats, setGroupReviewStats] = useState({}); // { [slug]: { totalRows, processedRows, pendingRows, ... } }

  // Load lightweight review stats for all groups to show per-group total entries
  useEffect(() => {
    const loadGroupStats = async (slugs) => {
      if (!Array.isArray(slugs) || slugs.length === 0) {
        setGroupReviewStats({});
        return;
      }
      try {
        const url = new URL(`${API_BASE_URL}/casc/v1/api/data-review/stats`, window.location.origin);
        url.searchParams.set('groups', slugs.join(','));
        url.searchParams.set('showAll', 'true');
        const resp = await authenticatedFetch(url.toString(), { method: 'GET' });
        if (resp.ok) {
          const data = await resp.json();
          if (data && data.success && data.stats) {
            const bySlug = {};
            const statsMap = data.stats;
            Object.keys(statsMap).forEach(slug => {
              const s = statsMap[slug] || {};
              bySlug[slug] = {
                totalRows: typeof s.totalRows === 'number' ? s.totalRows : 0,
                processedRows: typeof s.processedRows === 'number' ? s.processedRows : 0,
                pendingRows: typeof s.pendingRows === 'number' ? s.pendingRows : Math.max(0, (s.totalRows || 0) - (s.processedRows || 0)),
              };
            });
            setGroupReviewStats(prev => {
              // Avoid unnecessary state churn
              const same = slugs.every(slug => {
                const a = prev[slug];
                const b = bySlug[slug];
                return a && b && a.totalRows === b.totalRows && a.processedRows === b.processedRows && a.pendingRows === b.pendingRows;
              });
              return same ? prev : bySlug;
            });
          }
        }
      } catch (e) {
        // Silent fail; keep UI responsive
      }
    };

    const slugs = (groups || []).map(g => g.group_slug || g.slug || g.id).filter(Boolean);
    loadGroupStats(slugs);
  }, [groups]);


  // Load all prompts once for grouping under ingestion groups
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setPromptsLoading(true);
        const resp = await authenticatedFetch(`${API_BASE_URL}/casc/v1/api/prompts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        const json = await resp.json().catch(() => ({}));
        if (mounted && resp.ok && json?.success && Array.isArray(json.prompts)) {
          setAllPrompts(json.prompts);
        }
      } catch (_) {
        // ignore
      } finally {
        if (mounted) setPromptsLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const handleIngestGroup = useCallback(async (slug, options) => {
    if (!slug) return;
    setIngestLoadingByGroup(prev => ({ ...prev, [slug]: true }));
    try {
      const { jobId } = await ingestGroup(ingSuffix, slug, options);
      setResultMessage({ type: 'success', title: 'Ingestion started', text: `Job ${jobId} started for '${slug}'.` });
      // Begin polling progress
      let stopped = false;
      const poll = async () => {
        if (stopped) return;
        try {
          const p = await getIngestionProgress(ingSuffix, slug, jobId);
          const percent = typeof p?.percent === 'number' ? p.percent : (p?.total ? Math.round(((p.processed || 0) / (p.total || 1)) * 100) : 0);
          setIngestProgressByGroup(prev => ({ ...prev, [slug]: { percent, status: p?.status, processed: p?.processed, total: p?.total } }));
          if (p?.status === 'completed') {
            stopped = true;
            setResultMessage(buildIngestionResultMessage(p, slug));
            setIngestLoadingByGroup(prev => ({ ...prev, [slug]: false }));
            return;
          }
          if (p?.status === 'error') {
            stopped = true;
            setResultMessage(buildIngestionResultMessage(p, slug));
            setIngestLoadingByGroup(prev => ({ ...prev, [slug]: false }));
            return;
          }
        } catch (e) {
          // 404 means unknown job (expired/never existed) - stop polling
          if (e?.status === 404) {
            stopped = true;
            setResultMessage({ type: 'error', title: 'Ingestion job not found', text: 'The job may have expired or never existed.' });
            setIngestLoadingByGroup(prev => ({ ...prev, [slug]: false }));
            return;
          }
          // Other errors: show message and stop
          stopped = true;
          setResultMessage({ type: 'error', title: 'Progress error', text: e?.message || 'Failed to fetch progress' });
          setIngestLoadingByGroup(prev => ({ ...prev, [slug]: false }));
          return;
        }
        // Schedule next poll
        setTimeout(poll, 1500);
      };
      // Kick off polling
      poll();
    } catch (e) {
      setResultMessage({ type: 'error', title: 'Ingestion failed to start', text: e.message || 'Failed to start ingestion' });
      setIngestLoadingByGroup(prev => ({ ...prev, [slug]: false }));
    }
  }, [ingSuffix]);

  // CRUD operations removed - these are now only available in Settings > Ingestion
  // Users can only "Ingest" from the main horizon page

  useEffect(() => {
    // Prevent duplicate API calls (e.g., from React StrictMode or rapid tab switching)
    if (promptsLoadedRef.current || promptsLoadingRef.current) {
      return;
    }
    promptsLoadedRef.current = true;
    promptsLoadingRef.current = true;

    // Load prompt buttons for main page
    const loadPromptButtons = async () => {
      try {
        console.log('[MainPage] Loading prompt buttons...');
        const response = await authenticatedFetch(`${API_BASE_URL}/casc/v1/api/prompts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ page_location: 'main' }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.prompts) {
            setPromptButtons(data.prompts);
            console.log('[MainPage] Loaded', data.prompts.length, 'prompt buttons');
          }
        }
      } catch (error) {
        console.error('[MainPage] Error loading prompt buttons:', error);
        promptsLoadedRef.current = false; // Reset on error so it can retry
      } finally {
        promptsLoadingRef.current = false;
      }
    };

    loadPromptButtons();
  }, []);

  return (
    <div className="main-page">
      {resultMessage && (
        <div className="result-container" style={{ marginBottom: '20px' }}>
          <div className={`alert alert-${resultMessage.type}`}>
            <i className={`fas fa-${resultMessage.type === 'success' ? 'check-circle' : resultMessage.type === 'error' ? 'exclamation-circle' : resultMessage.type === 'warning' ? 'exclamation-triangle' : 'info-circle'}`}></i>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {resultMessage.title && <strong>{resultMessage.title}</strong>}
              {resultMessage.title && ' '}
              {resultMessage.text}
            </div>
          </div>
        </div>
      )}
      {/* Ingestion Groups (moved here from Settings) */}
      <div className="card full-width">
        <div className="card-title-with-action">
          <h2><i className="fas fa-layer-group"></i> Ingestion Groups</h2>
        </div>
        {groupsLoading && (
          <div className="loading"><i className="fas fa-spinner fa-spin"></i> Loading groups...</div>
        )}
        {groupsError && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-circle"></i> {groupsError}
          </div>
        )}
        {!groupsLoading && (!groups || groups.length === 0) && (
          <div className="text-muted">No ingestion groups found.</div>
        )}
        <div className="ingestion-groups-grid">
          {(groups || []).map(g => {
            const slug = g.group_slug || g.slug || g.id;
            const name = g.name || slug;
            // Only show Main Page tools (prompts) for this ingestion group
            const groupPromptsList = (allPrompts || [])
              .filter((p) => {
                const slugs = Array.isArray(p.ingestion_group_slugs) && p.ingestion_group_slugs.length
                  ? p.ingestion_group_slugs
                  : [p.ingestion_group_slug].filter(Boolean);
                return slugs.map(String).includes(String(slug));
              })
              .filter(p => p.page_location === 'main' || (p.id === 'ingestion' && !p.page_location));
            const busy = !!ingestLoadingByGroup[slug];
            const prog = ingestProgressByGroup[slug] || {};
            const provider = g.config?.api_source?.provider;
            return (
              <div className="ingestion-group-card" key={slug}>
                <div className="card-top-row">
                  <div className="title">{name}</div>
                  <div className="slug">{slug}</div>
                  <div className="entries-count">{groupReviewStats[slug]?.totalRows ?? 0} entries</div>
                </div>
                {isApiSourceGroup(g) && (
                  <div className="api-source-meta">
                    <span className="api-source-type-badge">
                      <i className="fas fa-plug" /> API ingestion
                      {provider ? ` · ${provider}` : ''}
                    </span>
                    {g.schedule_enabled && (
                      <span className="api-schedule-active-badge">
                        <i className="fas fa-clock" /> Periodic schedule active
                      </span>
                    )}
                  </div>
                )}
                <div className="card-bottom-row">
                  <div className="workflows-section">
                    {promptsLoading && <div className="text-muted">Loading…</div>}
                    {!promptsLoading && groupPromptsList.length === 0 && (
                      <div className="text-muted">No workflows</div>
                    )}
                    {groupPromptsList.map(p => (
                      <button
                        key={p.id}
                        className="btn btn-secondary prompt-button"
                        disabled={executingPromptId === p.id}
                        onClick={async () => {
                          try {
                            setExecutingPromptId(p.id);
                            setResultMessage(null);
                            // Build a group-aware URL explicitly for this group's request
                            let url;
                            try {
                              // Start with appendGroupToUrl to include any default handling, then override group
                              const base = appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/prompt-executions`);
                              const u = new URL(base, window.location.origin);
                              u.searchParams.set('group', slug);
                              url = u.toString();
                            } catch (_) {
                              const u = new URL(`${API_BASE_URL}/casc/v1/api/prompt-executions`, window.location.origin);
                              u.searchParams.set('group', slug);
                              url = u.toString();
                            }
                            const headers = await getAuthHeaders();
                            const resp = await fetch(url, {
                              method: 'POST',
                              headers,
                              body: JSON.stringify({ prompt_id: p.id })
                            });
                            if (resp.ok) {
                              const data = await resp.json();
                              if (data && data.id) {
                                // Notify queue to refresh immediately
                                try {
                                  window.dispatchEvent(new CustomEvent('executionRequestCreated', { detail: { id: data.id, prompt_id: p.id } }));
                                } catch {}
                                setResultMessage({
                                  type: 'success',
                                  title: 'Request created',
                                  text: `A new execution request was created for "${p.name || p.id}" and added to the queue.`
                                });
                                setActiveRequests(prev => prev.some(r => r.id === data.id) ? prev : [...prev, { id: data.id, name: p.name || p.id }]);
                              } else {
                                setResultMessage({ type: 'error', title: 'Unexpected response:', text: 'The server did not return a request id.' });
                              }
                            } else if (resp.status === 400) {
                              const err = await resp.json().catch(() => ({}));
                              setResultMessage({ type: 'error', title: 'Invalid request:', text: err.error || 'Invalid prompt id.' });
                            } else if (resp.status === 401) {
                              setResultMessage({ type: 'error', title: 'Unauthorized:', text: 'You must be signed in to execute prompts.' });
                            } else if (resp.status === 503) {
                              setResultMessage({ type: 'error', title: 'Service unavailable:', text: 'Please try again later.' });
                            } else {
                              const err = await resp.json().catch(() => ({}));
                              setResultMessage({ type: 'error', title: 'Error:', text: err.error || `Request failed with status ${resp.status}.` });
                            }
                          } catch (e) {
                            setResultMessage({ type: 'error', title: 'Network error:', text: e.message || 'Failed to create execution request.' });
                          } finally {
                            setExecutingPromptId(null);
                          }
                        }}
                      >
                        {executingPromptId === p.id ? 'Creating...' : (p.name || p.id)}
                      </button>
                    ))}
                  </div>
                  <div className="ingest-section">
                    <button
                      className="btn btn-primary btn-ingest"
                      disabled={busy}
                      onClick={() => {
                        if (isApiSourceGroup(g)) {
                          handleIngestGroup(slug, {});
                        } else {
                          setIngestDialogSlug(slug);
                        }
                      }}
                    >
                      {busy ? (<><i className="fas fa-spinner fa-spin"></i> {prog.status === 'validating_links' ? 'Validating links…' : <>Ingesting{typeof prog.percent === 'number' ? ` ${prog.percent}%` : '…'}</>}</>) : 'Ingest'}
                    </button>
                    {groupPromptsList.length > 0 && (
                      <small className="form-help" style={{ display: 'block', marginTop: 6 }}>
                        Ingest loads raw rows only. Click a workflow (e.g. extract) to fill Outputs.
                      </small>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Prompt execution queue — below ingestion groups so workflows stay primary */}
      <ExecutionQueue
        pageKey="main"
        horizonSuffix={ingSuffix}
        showCompletedRuns
        promptIds={Array.isArray(promptButtons) ? promptButtons.map(p => p.id) : []}
        promptNameById={Array.isArray(promptButtons) ? Object.fromEntries(promptButtons.map(p => [p.id, p.name])) : {}}
        onRequestComplete={(id) => setActiveRequests(prev => prev.filter(r => r.id !== id))}
      />

      {/* The 'Process Documents' section has been removed as per requirements. */}

      {/* Ingest Options Dialog */}
      {ingestDialogSlug && (() => {
        const dialogGroup = (groups || []).find(g => (g.group_slug || g.slug || g.id) === ingestDialogSlug);
        const dialogName = dialogGroup?.name || ingestDialogSlug;
        const mapping = dialogGroup?.config?.column_mapping;
        const hasLinks = Array.isArray(mapping) && mapping.some(m => m && m.link);
        const sourceType = dialogGroup?.config?.type || '';
        return (
          <IngestOptionsDialog
            isOpen={true}
            groupName={dialogName}
            sourceType={sourceType}
            hasLinkFields={hasLinks}
            onClose={() => setIngestDialogSlug(null)}
            onConfirm={(opts) => {
              setIngestDialogSlug(null);
              handleIngestGroup(ingestDialogSlug, opts);
            }}
          />
        );
      })()}

      {/* Ingestion Group Editor removed - CRUD operations only available in Settings > Ingestion */}
    </div>
  );
}

export default MainPage;

